import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from './api';
import { errorText } from '../i18n';
import { toast } from '@vatman/ui';

/*
 * Favorites (crates/server/src/api/favorites.rs, docs/navigation-v2-plan.md): what a person pins
 * in the menu — boards, projects, pages, spaces, teams, saved filters — in their own order.
 */

export type FavoriteKind = 'board' | 'project' | 'page' | 'space' | 'team' | 'filter';

export interface Favorite {
  kind: FavoriteKind;
  id: string;
  title: string;
  /** A project's or a filter's project key. */
  key?: string | null;
  /** A page's or a space's icon. */
  icon?: string | null;
  color?: string | null;
  personal?: boolean;
  post?: boolean;
  shared?: boolean;
}

export const favoritesKey = ['favorites'] as const;

export function useFavorites() {
  return useQuery({ queryKey: favoritesKey, queryFn: () => api.get<Favorite[]>('/favorites'), staleTime: 60_000 });
}

/** Where a favorite opens. */
export function favoriteLink(f: Pick<Favorite, 'kind' | 'id'>): string {
  switch (f.kind) {
    case 'board':
      return `/b/${f.id}`;
    case 'project':
      return `/t/${f.id}`;
    case 'page':
      return `/d/${f.id}`;
    case 'space':
      return `/s/${f.id}`;
    case 'team':
      return `/team/${f.id}`;
    case 'filter':
      return `/t/f/${f.id}`;
  }
}

/** Pin or unpin; the list comes back from the server in its order. */
export function useToggleFavorite() {
  const qc = useQueryClient();
  return async (kind: FavoriteKind, id: string, on: boolean) => {
    try {
      // A board's star is the same thing: its lists follow.
      const list = on ? await api.put<Favorite[]>(`/favorites/${kind}/${encodeURIComponent(id)}`) : await api.del<Favorite[]>(`/favorites/${kind}/${encodeURIComponent(id)}`);
      qc.setQueryData(favoritesKey, list);
      if (kind === 'board') void qc.invalidateQueries({ queryKey: ['boards'] });
    } catch (e) {
      toast(errorText(e instanceof ApiError ? e.code : 'internal'));
    }
  };
}

export function useIsFavorite(kind: FavoriteKind, id: string | null | undefined): boolean {
  const list = useFavorites();
  return !!id && !!list.data?.some((f) => f.kind === kind && f.id === id);
}

export async function reorderFavorites(qc: ReturnType<typeof useQueryClient>, items: Pick<Favorite, 'kind' | 'id'>[]) {
  try {
    qc.setQueryData(favoritesKey, await api.put<Favorite[]>('/favorites/order', { items: items.map(({ kind, id }) => ({ kind, id })) }));
  } catch (e) {
    toast(errorText(e instanceof ApiError ? e.code : 'internal'));
    void qc.invalidateQueries({ queryKey: favoritesKey });
  }
}
